import { CheckIcon, CopyIcon, HistoryIcon, LoadingIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import { Box, Chip, Stack, useTheme } from '@mui/material';
import MDEditor from '@uiw/react-md-editor';
import type { Root } from 'hast';
import { useCallback, useState } from 'react';
import rehypeSanitize from 'rehype-sanitize';
import type { Plugin } from 'unified';
import { AgentChatThinkingBlock } from './AgentChatThinkingBlock';
import { AgentChatToolBlock } from './AgentChatToolBlock';
import type { ChatMessage } from './agentChatTypes';
import { AgentChatRichBlock } from './richBlocks/AgentChatRichBlock';

/**
 * Rehype plugin that adds target="_blank" and rel="noopener noreferrer" to all
 * anchor tags so that links in chat messages open in a new browser tab instead
 * of navigating away from the current page. Runs AFTER rehype-sanitize so the
 * attributes are added to already-sanitised output.
 */
const rehypeExternalLinks: Plugin<[], Root> = () => {
  return (tree: Root) => {
    function visit(node: Root | Root['children'][number]) {
      if (node.type === 'element') {
        if (node.tagName === 'a') {
          const { properties } = node;
          properties.target = '_blank';
          properties.rel = 'noopener noreferrer';
        }
        node.children.forEach(visit);
      } else if ('children' in node) {
        node.children.forEach(visit);
      }
    }
    visit(tree);
  };
};

/**
 * Renders a single chat message bubble.
 * - User messages appear right-aligned in a primary-coloured chip.
 * - Agent messages render an ordered sequence of content blocks so tool
 *   calls appear interspersed with text in their actual execution order.
 * - Agent text blocks use MDEditor.Markdown for rich formatting.
 */
/**
 * Extracts copyable plain text from a message.
 * User messages use the text field; agent messages concatenate all text blocks.
 */
function getCopyableText(message: ChatMessage): string {
  if (message.role === 'user') return message.text ?? '';
  return (
    message.blocks
      ?.filter((b) => b.type === 'text')
      .map((b) => (b as { content: string }).content)
      .join('\n\n') ?? ''
  );
}

type AgentChatMessageProps = {
  message: ChatMessage;
  onRespondToBlock?: (blockId: string, answer: Record<string, unknown>) => void;
};

export const AgentChatMessage = ({ message, onRespondToBlock }: AgentChatMessageProps) => {
  const { palette, typography } = useTheme();

  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  /** Copy the message text to the clipboard and flash a check icon briefly */
  const handleCopy = useCallback(() => {
    const text = getCopyableText(message);
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((error) => {
        console.error('Failed to copy message to clipboard:', error);
      });
  }, [message]);

  // Only show copy button when there is copyable text and the message isn't still streaming
  const showCopy = !message.streaming && !!getCopyableText(message);

  return (
    <Stack
      sx={{
        justifySelf: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
        // Show copy button on hover
        '&:hover .copy-btn': { opacity: 1 },
        maxWidth: '90%',
        minWidth: 0,
        overflow: 'hidden',
        p: 2,
        position: 'relative',
        color: palette.agentChat.message[message.role].foreground.default,
        backgroundColor: palette.agentChat.message[message.role].background,
        border: '1px solid',
        borderColor: palette.agentChat.message[message.role].border,
        borderRadius: 2,
        // Queued messages get reduced opacity and a dashed border to show they're pending
        ...(message.queued && {
          bgcolor: 'agentChat.message.user.queued.background',
          border: '1px dashed',
          borderColor: 'agentChat.message.user.queued.border',
          color: 'agentChat.message.user.queued.foreground.default',
        }),
      }}
      data-testid={`agent-chat-message-${message.role}`}
      gap={1}
    >
      {/* User messages show plain text + optional pasted images */}
      {isUser && message.text && (
        <Typography
          variant="caption2"
          sx={{ whiteSpace: 'pre-wrap', fontStyle: message.queued ? 'italic' : 'normal' }}
        >
          {message.text}
        </Typography>
      )}

      {isUser && message.images && message.images.length > 0 && (
        <Box
          sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}
          data-testid="user-message-images"
        >
          {message.images.map((src, idx) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                maxWidth: 200,
                border: '1px solid',
                borderColor: 'rgba(255,255,255,0.2)',
              }}
            >
              <img
                src={src}
                alt={`Message attachment ${idx + 1}`}
                style={{ display: 'block', width: '100%', height: 'auto' }}
                data-testid={`user-image-${idx}`}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Agent messages render blocks in order */}
      {!isUser &&
        message.blocks?.map((block) => {
          if (block.type === 'thinking') {
            return <AgentChatThinkingBlock key={block.content} content={block.content} />;
          }
          if (block.type === 'text') {
            return (
              <Box
                key={block.content}
                data-color-mode={palette.mode}
                sx={{
                  // Prevent the markdown container from exceeding its parent width
                  minWidth: 0,
                  '& .wmde-markdown': {
                    background: 'transparent',
                    fontSize: '0.875rem',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  },
                  // Tables and other block elements must not exceed container
                  '& .wmde-markdown table': {
                    display: 'block',
                    overflowX: 'auto',
                    maxWidth: '100%',
                  },
                  // Code blocks inside markdown must scroll horizontally rather than
                  // overflowing outside the chat panel boundary. The wmde-markdown
                  // renderer wraps code in <pre><code> — the <pre> defaults to
                  // overflow: visible which allows content to bleed out of the panel.
                  // Setting overflow-x: auto on <pre> gives it a scrollbar instead.
                  '& .wmde-markdown pre': {
                    overflowX: 'auto',
                    maxWidth: '100%',
                  },
                  '& .wmde-markdown p': {
                    ...typography.body2,
                  },
                }}
              >
                <MDEditor.Markdown
                  source={block.content}
                  rehypePlugins={[[rehypeSanitize], rehypeExternalLinks]}
                />
              </Box>
            );
          }
          if (block.type === 'tool_call') {
            return <AgentChatToolBlock key={block.toolUseId} block={block} />;
          }
          if (block.type === 'rich_block') {
            return (
              <AgentChatRichBlock key={block.blockId} block={block} onRespond={onRespondToBlock} />
            );
          }
          return null;
        })}

      {/* Timestamp — small time indicator for message order */}
      <Group
        sx={{
          alignItems: 'center',
          gap: 2,
          mt: 1,
          justifyContent:
            isUser && message.queued ? 'space-between' : isUser ? 'flex-end' : 'flex-start',
        }}
      >
        {/* Queued indicator chip — shown on messages waiting to be sent */}
        {message.queued && (
          <Chip
            icon={<HistoryIcon size={12} />}
            label="Queued"
            size="small"
            data-testid="agent-chat-queued-chip"
            sx={{
              paddingY: 0,
              paddingX: 1,
              height: 'min-content',
              bgcolor: 'agentChat.message.user.queued.border',
              borderRadius: 1,
              gap: 1,
              '& .MuiChip-icon': {
                color: 'agentChat.message.user.queued.foreground.warning',
                m: 0,
                p: 0,
              },
              '& .MuiChip-label': {
                m: 0,
                p: 0,
                pr: 0.5,
                ...typography.caption2,
                color: 'agentChat.message.user.queued.foreground.warning',
              },
            }}
          />
        )}
        {message.timestamp && !message.streaming && (
          <Typography
            variant="navigation"
            color={palette.agentChat.message[message.role].foreground.light}
            data-testid="agent-chat-timestamp"
          >
            {new Date(message.timestamp).toLocaleTimeString()}
          </Typography>
        )}
      </Group>

      {/* Streaming spinner while message is still accumulating */}
      {message.streaming && (
        <Group sx={{ alignItems: 'center', gap: 1, mt: 1 }}>
          <LoadingIcon size={12} />
          <Typography
            variant="caption2"
            color={palette.agentChat.message[message.role].foreground.light}
          >
            Thinking…
          </Typography>
        </Group>
      )}

      {/* Copy-to-clipboard button — visible on hover */}
      {showCopy && (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
          <IconButton
            Icon={copied ? CheckIcon : CopyIcon}
            className="copy-btn"
            size={14}
            onClick={handleCopy}
            data-testid="agent-chat-copy-btn"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.15s',
              color: isUser ? 'primary.contrastText' : 'grey.500',
              '&:hover': {
                color: isUser ? 'primary.contrastText' : 'grey.300',
                bgcolor: isUser ? 'rgba(255,255,255,0.15)' : 'action.hover',
              },
              p: 0.5,
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};
